import React from 'react';
import {
  C,
  CARD_W, VOTE_CARD_H, VOTE_CARD_GAP, CIRCLE_R, PLUS_R, TRIG_W,
  BRANCH_VSEP, BRANCH_STEM,
  normalizeImgField,
} from '../flowCanvas.constants';
import VoteCardNode from './VoteCardNode';
import TriggerNode  from './TriggerNode';

// ─── Wave graphics node — stacked vote cards + branch wiring + hashtag trigger ─
//
// Props:
//   step            – the waveGraphics step object
//   stepX           – BFS-computed X centre for card stack
//   teamCY          – vertical centre for this team's row
//   parentStepX     – X centre of the parent team circle (null = no parent)
//   hashStep        – the linked hashtag trigger step (null = none)
//   hashCY          – computed Y centre for the hashtag trigger (null = none)
//   hashNodeX       – BFS X centre for the hashtag trigger node (null = none)
//   wVals           – all allValues for this step (unfiltered — used for absoluteIndex)
//   wItems          – filtered values for this team (what is rendered)
//   activeStepId    – currently selected step id
//   activeGraphicIndex – currently selected graphic index
//   assetFolder / campaignId – for asset URL resolution
//   onGraphicClick(absIdx)  – fired when a VoteCardNode is clicked
//   onAddGraphicClick()     – fired when the + circle on the "Vote image" branch is clicked
//   onHashtagAddClick()     – fired when the + circle on the "Vote hashtag" branch is clicked
//   onHashtagNodeClick()    – fired when the TriggerNode itself is clicked
//   onRemoveGraphic         – passed directly to each VoteCardNode
export default function WaveGraphicsNode({
  step, stepX, teamCY,
  parentStepX,
  hashStep, hashCY, hashNodeX,
  wVals, wItems,
  activeStepId, activeGraphicIndex,
  assetFolder, campaignId,
  onGraphicClick, onAddGraphicClick, onHashtagAddClick, onHashtagNodeClick,
  canEditHashtags = false,
  onCommitHashtags,
  hashtagFocusNonce = 0,
  onRemoveGraphic,
  onDraftHeightChange,
}) {
  const wFilter   = step.customisationConfig?.meta?.teamFilter;
  const wAssign   = step.customisationConfig?.meta?.teamAssign ?? wFilter;
  const teamLabel = wAssign === 0 ? 'Team 1' : wAssign === 1 ? 'Team 2' : 'All Teams';

  const count    = Math.max(wItems.length, 1);
  const totalH   = count * (VOTE_CARD_H + VOTE_CARD_GAP) - VOTE_CARD_GAP;

  const hasParent  = parentStepX !== null && parentStepX !== undefined;
  const teamRightX = hasParent ? parentStepX + CIRCLE_R : stepX - CARD_W / 2 - 50 + CIRCLE_R;

  const stackTopY         = teamCY - totalH / 2;
  const imgBranchOriginY  = teamCY - BRANCH_VSEP;
  const hashBranchOriginY = teamCY + BRANCH_VSEP;

  const cardCentreX = stepX;
  const cardLeftX   = cardCentreX - CARD_W / 2;
  const branchMidX  = Math.max(teamRightX + BRANCH_STEM, (teamRightX + cardLeftX) / 2);

  const firstCardY = stackTopY + VOTE_CARD_H / 2;
  const lastCardY  = stackTopY + (Math.max(wItems.length, 1) - 1) * (VOTE_CARD_H + VOTE_CARD_GAP) + VOTE_CARD_H / 2;

  const plusX = (teamRightX + branchMidX) / 2;
  const plusY = imgBranchOriginY;

  const hashLeftX = hashNodeX !== null && hashNodeX !== undefined ? hashNodeX - TRIG_W / 2 : null;

  const imgComplete = wItems.length > 0 && wItems.every(v => normalizeImgField(v.filename).name);
  const imgColor    = imgComplete ? C.purple : wItems.length > 0 ? C.orange : C.textDim;
  const hashColor   = '#a78bfa';

  return (
    <React.Fragment key={step.stepId}>

      {/* ── "Vote image" branch: team circle → + circle → stacked vote cards ── */}
      {hasParent && (
        <g style={{ pointerEvents: 'none' }}>
          {/* Horizontal stem from circle right edge to branchMidX */}
          <line x1={teamRightX} y1={imgBranchOriginY} x2={branchMidX} y2={imgBranchOriginY}
            stroke={imgColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
          {/* Branch label raised above the + circle */}
          <text x={(teamRightX + branchMidX) / 2} y={imgBranchOriginY - PLUS_R - 8}
            textAnchor="middle" fontSize="9.5" fontWeight="600" fill={imgColor}
            style={{ userSelect: 'none' }}>Vote image</text>
          {/* Vertical jog from stem to firstCardY (when needed) */}
          {Math.abs(imgBranchOriginY - firstCardY) > 2 && (
            <line x1={branchMidX} y1={imgBranchOriginY} x2={branchMidX} y2={firstCardY}
              stroke={imgColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
          )}
          {/* Vertical spine spanning all cards */}
          {wItems.length > 1 && (
            <line x1={branchMidX} y1={firstCardY} x2={branchMidX} y2={lastCardY}
              stroke={imgColor} strokeWidth="1.5" strokeOpacity="0.5" />
          )}
          {/* Horizontal spur + arrival dot for each card */}
          {(wItems.length > 0 ? wItems : [null]).map((_, i) => {
            const cy = stackTopY + i * (VOTE_CARD_H + VOTE_CARD_GAP) + VOTE_CARD_H / 2;
            return (
              <React.Fragment key={i}>
                <line x1={branchMidX} y1={cy} x2={cardLeftX} y2={cy}
                  stroke={imgColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
                <circle cx={cardLeftX} cy={cy} r="3" fill={imgColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
        </g>
      )}

      {/* ── + add-graphic circle on "Vote image" branch stub ── */}
      {hasParent && (
        <g onClick={onAddGraphicClick} style={{ cursor: 'pointer' }}>
          <circle cx={plusX} cy={plusY} r={PLUS_R} fill={C.green} />
          <text x={plusX} y={plusY + 1}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="16" fontWeight="300" fill="#fff"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
        </g>
      )}

      {/* ── "Vote hashtag" branch: team circle → + circle → hashtag trigger ── */}
      {hasParent && hashStep && hashLeftX !== null && hashCY !== null && (() => {
        const hashBranchMidX = Math.min(
          Math.max(teamRightX + BRANCH_STEM, (teamRightX + hashLeftX) / 2),
          branchMidX - 20,
        );
        const sameY      = Math.abs(hashBranchOriginY - hashCY) < 2;
        const hashPlusX  = (teamRightX + hashBranchMidX) / 2;
        const hashPlusY  = hashBranchOriginY;
        const d = sameY
          ? `M ${teamRightX} ${hashBranchOriginY} H ${hashLeftX}`
          : `M ${teamRightX} ${hashBranchOriginY} H ${hashBranchMidX} V ${hashCY} H ${hashLeftX}`;
        return (
          <>
            <g style={{ pointerEvents: 'none' }}>
              <path d={d} stroke={hashColor} strokeWidth="1.5" fill="none"
                strokeOpacity="0.7" strokeLinecap="square" />
              <circle cx={hashLeftX} cy={hashCY} r="3" fill={hashColor} fillOpacity="0.9" />
              <text x={hashPlusX} y={hashBranchOriginY - PLUS_R - 8}
                textAnchor="middle" fontSize="9.5" fontWeight="600" fill={hashColor}
                style={{ userSelect: 'none' }}>Vote hashtag</text>
            </g>
            {/* + circle on hashtag branch */}
            <g onClick={onHashtagAddClick} style={{ cursor: 'pointer' }}>
              <circle cx={hashPlusX} cy={hashPlusY} r={PLUS_R} fill={C.green} />
              <text x={hashPlusX} y={hashPlusY + 1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize="16" fontWeight="300" fill="#fff"
                style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
            </g>
          </>
        );
      })()}

      {/* ── Individual VoteCardNode per graphic, stacked vertically ── */}
      {wItems.length > 0 ? (() => {
        const totalTeamFreq = wItems.reduce((s, v) => s + (v.appearFrequency ?? 100), 0) || 100;
        return wItems.map((item, i) => {
          const cardY       = stackTopY + i * (VOTE_CARD_H + VOTE_CARD_GAP) + VOTE_CARD_H / 2;
          const absoluteIndex = wVals.indexOf(item);
          const isSel       = activeStepId === step.stepId && activeGraphicIndex === absoluteIndex;
          return (
            <VoteCardNode
              key={i}
              graphicItem={item}
              graphicIndex={i}
              teamLabel={teamLabel}
              selected={isSel}
              onClick={() => onGraphicClick(absoluteIndex)}
              overrideX={cardCentreX}
              overrideY={cardY}
              assetFolder={assetFolder}
              campaignId={campaignId}
              teamTotalFreq={totalTeamFreq}
              onDelete={wItems.length > 1 && onRemoveGraphic
                ? () => onRemoveGraphic(step, absoluteIndex)
                : null}
            />
          );
        });
      })() : (
        <g key="empty" onClick={onAddGraphicClick} style={{ cursor: 'pointer' }}>
          <rect x={cardCentreX - CARD_W / 2 + 2} y={teamCY - VOTE_CARD_H / 2 + 4}
            width={CARD_W} height={VOTE_CARD_H} rx="8" fill="#000" fillOpacity="0.3" />
          <rect x={cardCentreX - CARD_W / 2} y={teamCY - VOTE_CARD_H / 2}
            width={CARD_W} height={VOTE_CARD_H} rx="8"
            fill={C.card} stroke={C.orange} strokeWidth="1.5" strokeDasharray="5 3" />
          <text x={cardCentreX} y={teamCY - 16} textAnchor="middle" fontSize="11" fontWeight="600" fill={C.orange}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>No hand graphic yet</text>
          <text x={cardCentreX} y={teamCY + 4} textAnchor="middle" fontSize="9" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>Press</text>
          <text x={cardCentreX} y={teamCY + 18} textAnchor="middle" fontSize="20" fontWeight="300" fill="#4ade80"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
          <text x={cardCentreX} y={teamCY + 36} textAnchor="middle" fontSize="9" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>to add a hand</text>
        </g>
      )}

      {/* ── Right-exit from image stack to merge spine, joining the hashtag merge connector ── */}
      {hasParent && hashStep && hashCY !== null && (() => {
        // The hashtag merge connector group draws a shared spine at approximately
        // hashNodeX + TRIG_W/2 + 34 (= mergeX). Connect the card stack's right
        // edge to that same spine at teamCY, then jog down to hashCY so the two
        // inputs (image cards + hashtag) visually converge on the same merge point.
        const mergeSpineX = hashNodeX + TRIG_W / 2 + 34;
        const d = `M ${cardCentreX + CARD_W / 2} ${teamCY} H ${mergeSpineX} V ${hashCY}`;
        return (
          <path key="wave-exit" d={d} stroke={imgColor} strokeWidth="1.5" fill="none"
            strokeOpacity="0.7" strokeLinecap="square" style={{ pointerEvents: 'none' }} />
        );
      })()}

      {/* ── Hashtag TriggerNode rendered inline so its Y tracks live stack height ── */}
      {hashStep && hashCY !== null && (
        <TriggerNode
          key={hashStep.stepId}
          step={hashStep}
          selected={activeStepId === hashStep.stepId}
          onClick={onHashtagNodeClick}
          centreY={teamCY}
          overrideX={hashNodeX}
          overrideY={hashCY}
          editable={canEditHashtags}
          onCommitHashtags={onCommitHashtags}
          focusNonce={hashtagFocusNonce}
          onDraftHeightChange={onDraftHeightChange}
        />
      )}

    </React.Fragment>
  );
}
